import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  TextField,
  Button,
  useTheme,
  Chip,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  Paper,
  Avatar,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  AccessTime,
  Send,
  Facebook,
  Twitter,
  LinkedIn,
  Instagram,
  CheckCircle,
  ArrowForward,
  Business,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setSubmitted(true);
      setLoading(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    }, 1500);
  };

  const contactInfo = [
    { icon: <LocationOn />, title: 'Visit Us', details: ['341 Beyers Naude Drive', 'Northcliff Johannesburg, South Africa, 2195'], color: '#e60108' },
    { icon: <Phone />, title: 'WhatsApp', details: ['WhatsApp SOUTH AFRICA'], color: '#191919' },
    { icon: <Email />, title: 'Email', details: ['Contact SOUTH AFRICA'], color: '#e60108' },
    { icon: <AccessTime />, title: 'Hours', details: ['Mon-Fri: 9am - 6pm SAST'], color: '#a0a0a0' },
  ];

  const offices = [
    { city: 'Johannesburg', country: 'SOUTH AFRICA', address: '341 Beyers Naude Drive, Northcliff Johannesburg, South Africa, 2195', flag: '🇿🇦', hours: 'Mon-Fri: 9am - 6pm SAST' },
  ];

  return (
    <Box>
      {/* Hero Section with Image */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Worldwide communications.jpg"
          alt="Worldwide Communications"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Contact Us"
              icon={<Business sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Let's Connect
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Whether you have a question, need a quote, or want to discuss a project,
              our team is ready to help across 150+ countries worldwide.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card sx={{ borderRadius: 4, p: 4 }}>
                <Typography variant="h4" gutterBottom fontWeight="800" sx={{ color: '#191919' }}>
                  Send us a Message
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 4 }}>
                  Fill out the form below and we'll get back to you within 24 hours.
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#e60108' },
                            '&.Mui-focused fieldset': { borderColor: '#e60108' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#e60108' },
                            '&.Mui-focused fieldset': { borderColor: '#e60108' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#e60108' },
                            '&.Mui-focused fieldset': { borderColor: '#e60108' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#e60108' },
                            '&.Mui-focused fieldset': { borderColor: '#e60108' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Your Message"
                        name="message"
                        multiline
                        rows={6}
                        value={formData.message}
                        onChange={handleChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': { borderColor: '#e60108' },
                            '&.Mui-focused fieldset': { borderColor: '#e60108' },
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={loading}
                          endIcon={!loading && <Send />}
                          sx={{
                            py: 1.5,
                            fontSize: '1.1rem',
                            bgcolor: '#e60108',
                            '&:hover': { bgcolor: '#b80006' },
                          }}
                        >
                          {loading ? 'Sending...' : 'Send Message'}
                        </Button>
                      </motion.div>
                    </Grid>
                  </Grid>
                </form>
              </Card>
            </motion.div>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Stack spacing={3}>
                {contactInfo.map((info, idx) => (
                  <Card key={idx} sx={{ p: 3, borderRadius: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      <Avatar
                        sx={{
                          width: 50,
                          height: 50,
                          bgcolor: info.color + '15',
                          color: info.color,
                        }}
                      >
                        {info.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                          {info.title}
                        </Typography>
                        {info.details.map((detail, i) => (
                          <Typography key={i} variant="body2" sx={{ color: '#a0a0a0' }}>
                            {detail}
                          </Typography>
                        ))}
                      </Box>
                    </Stack>
                  </Card>
                ))}

                {/* Emergency Support Card */}
                <Card sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #e60108 0%, #191919 100%)', color: '#fefefe' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Emergency Support
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    Need immediate assistance? Our emergency support team is available 24/7.
                  </Typography>
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{ bgcolor: '#fefefe', color: '#e60108', '&:hover': { bgcolor: '#fefefe' } }}
                      href="tel:+18881234567"
                    >
                      Call Emergency Hotline
                    </Button>
                  </motion.div>
                </Card>

                {/* Social Links Card */}
                <Card sx={{ p: 3, borderRadius: 3 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                    Follow Us
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <IconButton sx={{ bgcolor: '#f5f5f5', color: '#191919', '&:hover': { bgcolor: '#1877F2', color: '#fefefe' } }}>
                      <Facebook />
                    </IconButton>
                    <IconButton sx={{ bgcolor: '#f5f5f5', color: '#191919', '&:hover': { bgcolor: '#1DA1F2', color: '#fefefe' } }}>
                      <Twitter />
                    </IconButton>
                    <IconButton sx={{ bgcolor: '#f5f5f5', color: '#191919', '&:hover': { bgcolor: '#0077B5', color: '#fefefe' } }}>
                      <LinkedIn />
                    </IconButton>
                    <IconButton sx={{ bgcolor: '#f5f5f5', color: '#191919', '&:hover': { bgcolor: '#E4405F', color: '#fefefe' } }}>
                      <Instagram />
                    </IconButton>
                  </Stack>
                </Card>
              </Stack>
            </motion.div>
          </Grid>
        </Grid>

        {/* Global Offices */}
        <Box sx={{ mt: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Our Location"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Our Global Office
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}>
              We're here to serve you from our headquarters in South Africa
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {offices.map((office, idx) => (
              <Grid item xs={12} sm={8} md={6} key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                    <Typography variant="h1" sx={{ fontSize: '3rem', mb: 1 }}>
                      {office.flag}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: '#191919' }}>
                      {office.city}
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#a0a0a0' }} gutterBottom>
                      {office.country}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0', mt: 1 }}>
                      {office.address}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#e60108', mt: 2, fontWeight: 600 }}>
                      🕒 {office.hours}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {/* Map Section */}
      <Box sx={{ height: 400 }}>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3581.048659073307!2d27.9940964!3d-26.1628853!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e9573f1b7e8b9b7%3A0x5c78b4b8b9b7b8b7!2sJohannesburg%2C%20South%20Africa!5e0!3m2!1sen!2sus!4v1644262071847!5m2!1sen!2sus"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          title="Worldwide Communication Location Map"
        />
      </Box>

      <Snackbar open={submitted} autoHideDuration={6000} onClose={() => setSubmitted(false)}>
        <Alert severity="success" sx={{ width: '100%', bgcolor: '#00C853', color: '#fefefe' }}>
          Thank you for your message! We'll get back to you soon.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact;